import React, { FC } from 'react';

import { faClock, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'epn-ui/umd/dist/components/Button';
import { faBan } from '@fortawesome/pro-regular-svg-icons/faBan';
import useI18n from '@i18n';

import routes from '@config/routes.json';
import BaseSemanticLink from '@base/BaseSemanticLink';

export interface IOfferButtonGroupProps {
    offerId: number;
    offerUrl?: string;
    onConnect: () => void;
    showTheReason: () => void;
    connection: {
        id?: string;
        offerId?: string;
        status?: 'allow' | 'ban' | 'review' | 'recheck' | 'waiting' | null;
        comment?: string | null;
    };
    isAuth: boolean;
    className?: string;
}

const OfferButtonGroup: FC<IOfferButtonGroupProps> = ({
    offerId,
    offerUrl,
    onConnect,
    showTheReason,
    connection,
    isAuth,
}) => {
    const { t, locale } = useI18n('Offers');
    const { t: tCommon } = useI18n('Common');

    const status = connection?.status || null;

    const saveOfferId = (): void => {
        localStorage.setItem('affiliateLinksOfferId', offerId.toString());
        localStorage.setItem('affiliateLinksPlacementId', connection?.id.toString());
    };

    const getLinkFromComment = (comment: string): string => {
        const matches = comment?.match(/href="([^"]+)"/);
        return matches?.[1] || `/${locale}${routes.support.open}`;
    };

    return (
        <>
            {(status === null || status === 'allow') && (
                <div className="flex flex-wrap mt-4 gap-2">
                    <div className={offerUrl ? 'w-[calc(100%-48px)]' : 'w-full'}>
                        {status === null && !isAuth && (
                            <Button
                                type="primary"
                                className="w-full !bg-blueGray-800 hover:!bg-blueGray-600"
                                href={`/${locale}/auth`}
                            >
                                {tCommon('Connect')}
                            </Button>
                        )}
                        {status === null && isAuth && (
                            <Button
                                type="primary"
                                className="w-full !bg-blueGray-800 hover:!bg-blueGray-600"
                                onClick={onConnect}
                            >
                                {tCommon('Connect')}
                            </Button>
                        )}

                        {status === 'allow' && (
                            <BaseSemanticLink to={routes.tools.affiliateLinks} target="_blank">
                                <Button type="primary" className="w-full" onClick={saveOfferId}>
                                    {t('Create link')}
                                </Button>
                            </BaseSemanticLink>
                        )}
                    </div>
                    {offerUrl && (
                        <Button className="w-10 h-10" href={`/${locale}/offers/${offerUrl}`}>
                            <FontAwesomeIcon className="" icon={faInfoCircle} />
                        </Button>
                    )}
                </div>
            )}
            {(status === 'ban' || status === 'review' || status === 'waiting') && (
                <div className="mt-4">
                    {status === 'review' && (
                        <div className="w-full text-center bg-blueGray-100 text-blueGray-800 text-[14px] py-[9px] rounded-[6px]">
                            <FontAwesomeIcon icon={faClock} className="text-blue-500 mr-[8px]" />
                            {t('Under review')}
                        </div>
                    )}

                    {status === 'waiting' && getLinkFromComment(connection?.comment) && (
                        <Button
                            type="primary"
                            className="w-full"
                            href={getLinkFromComment(connection?.comment)}
                            target="_blank"
                            disabled={!connection?.comment}
                        >
                            {t('Need clarification')}
                        </Button>
                    )}

                    {status === 'ban' && (
                        <Button
                            type="primary"
                            className="w-full text-center !bg-red-100 !text-blueGray-800 text-[14px] py-[9px] rounded-[6px]"
                            onClick={showTheReason}
                        >
                            <FontAwesomeIcon icon={faBan} className="text-red-500 mr-[8px]" />
                            {t('Declined')}
                        </Button>
                    )}
                </div>
            )}
        </>
    );
};

export default OfferButtonGroup;
